import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useDeleteAutoplayTest } from '@/hooks/useAutoplayTest';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface FinalizeAutoplayTestModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  testId?: string;
}

export const FinalizeAutoplayTestModal: React.FC<FinalizeAutoplayTestModalProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
  testId,
}) => {
  useLanguage();
  const { mutateAsync: deleteTest, isPending: isDeleting } = useDeleteAutoplayTest();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ab_test', 'edit');

  useEffect(() => {
    if (isOpen && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para finalizar testes A/B"));
      onOpenChange(false);
    }
  }, [isOpen, canEdit, onOpenChange]);

  const handleConfirm = async () => {
    if (testId) {
      try {
        await deleteTest(testId);
        onConfirm();
        onOpenChange(false);
      } catch (error) {
        // O erro já é tratado pelo hook
        console.error('Erro ao deletar teste:', error);
      }
    } else {
      // Se não tem testId, apenas executa o callback (comportamento local)
      onConfirm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none rounded-2xl">
        <DialogHeader>
          <DialogTitle>{translateInterfaceText("Finalizar teste A/B?")}</DialogTitle>
          <DialogDescription>
            {translateInterfaceText("O teste será encerrado e a variação principal será aplicada. Esta ação não pode ser desfeita.")}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? translateInterfaceText("Deletando...") : translateInterfaceText("Finalizar Teste")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
